import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import * as Haptics from 'expo-haptics';
import React, { useState } from "react";
import { View, TouchableOpacity, ScrollView, ActivityIndicator, Platform, Alert } from "react-native";
import { SafeAreaView } from 'react-native-safe-area-context';
import tw from "twrnc";

import { DSText } from '../../design-system/components/DSText';
import { useAppTheme } from '../../design-system/ThemeProvider';
import { spacing, radius, shadows } from '../../design-system/tokens';
import { resolveGroupContextMeta } from "../../features/multi-player/utils/groupMeta";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { useMultiplayerStore } from "../../features/quiz-core/multiplayerStore";
import { useGameStore } from "../../features/quiz-core/useStore";
import { t } from "../../i18n/translations";
import { RootStackParamList } from "../../navigation/types";

import { iqesColors } from "./iqesDesignSystem";

const shellColors = {
  canvas: iqesColors.canvas,
  panel: 'rgba(8,10,24,0.94)',
  panelRaised: 'rgba(16,12,34,0.96)',
  border: 'rgba(246,190,58,0.34)',
  borderStrong: 'rgba(246,190,58,0.54)',
  gold: iqesColors.gold,
  goldBright: iqesColors.goldBright,
  goldSoft: iqesColors.goldSoft,
  ivory: iqesColors.ivory,
  muted: 'rgba(255,248,232,0.72)',
};

const showDebugModeBadge = false;

// Unified props for Solo, Multiplayer, and Challenge modes
interface UnifiedDifficultyScreenProps {
  mode?: 'solo' | 'multiplayer' | 'challenge';
  category?: string;
  categoryId?: number;
  testament?: string;
  onDifficultySelect?: (difficulty_id: number) => void;
  challengeMode?: boolean;
  opponent?: { id: string; name: string }; // For Challenge mode
}

type DifficultyScreenNavigationProp = StackNavigationProp<
  RootStackParamList,
  "Difficulty" | "MultiplayerDifficulty" | "ChallengeDifficulty"
>;

type DifficultyScreenRouteProp = RouteProp<
  RootStackParamList,
  "Difficulty" | "MultiplayerDifficulty" | "ChallengeDifficulty"
>;

/**
 * UnifiedDifficultyScreen - Consolidated Difficulty Selection
 *
 * This component consolidates Solo Play, Multiplayer, and Challenge Difficulty screens,
 * reducing code duplication by ~80% while maintaining all functionality.
 *
 * Key features:
 * - Mode detection from route name or explicit prop ('solo' | 'multiplayer' | 'challenge')
 * - Shared difficulty options and UI logic
 * - Mode-specific navigation (Solo/Multiplayer/Challenge flows)
 * - Challenge mode support with opponent parameter
 * - Full internationalization support
 * - Haptic feedback for multiplayer mode
 * - Enhanced visual feedback and loading states
 * - Maintains all existing animations and styling
 */
const UnifiedDifficultyScreen: React.FC<UnifiedDifficultyScreenProps> = ({
  mode: explicitMode,
  category: propCategory,
  categoryId: propCategoryId,
  testament: propTestament,
  onDifficultySelect,
  challengeMode: propChallengeMode,
  opponent
}) => {
  const { colors } = useAppTheme();
  const navigation = useNavigation<DifficultyScreenNavigationProp>();
  const route = useRoute<DifficultyScreenRouteProp>();
  const { language } = useLanguageStore();

  // Determine mode from route name if not explicitly provided
  const mode = explicitMode ||
    (route.name === 'MultiplayerDifficulty' ? 'multiplayer' :
     route.name === 'ChallengeDifficulty' ? 'challenge' : 'solo');

  // Extract parameters with prop fallbacks (for flexible usage)
  const category = propCategory ?? route.params?.category;
  const categoryId = propCategoryId ?? route.params?.categoryId;
  const testament = propTestament ?? route.params?.testament;
  const challengeMode = propChallengeMode ?? (route.params as any)?.challengeMode ?? false;

  // Mode-specific parameters - safely handle union types
  const params = route.params || {};
  const rematch = (params as any)?.rematch;
  const oldRoomId = (params as any)?.oldRoomId;
  const familyId = (params as any)?.familyId;
  const friendsGroupId = (params as any)?.friendsGroupId;
  const routeOpponent = (params as any)?.opponent;
  const challengeOpponent = opponent || routeOpponent;
  const roomType = familyId ? 'family' : friendsGroupId ? 'friends' : 'friends';
  const groupMeta = resolveGroupContextMeta(
    {
      familyId,
      friendsGroupId,
    },
    language,
  );

  // State management
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Store access
  const { setQuestions: _setQuestions } = useGameStore();
  const createRoom = useMultiplayerStore((s) => s.createRoom);
  const userProfile = require("../../features/quiz-core/useStore").useUserProfile();

  // Constants
  const questionCount = 10; // Fixed at 10
  const difficultyTitle =
    mode === 'multiplayer'
      ? t('multiplayer_lobby.setupDifficultyTitle', language)
      : t('coreUI.select_your_challenge', language) || t('select_your_challenge', language) || 'Select Your Challenge';
  const difficultySubtitle =
    mode === 'multiplayer'
      ? t('multiplayer_lobby.setupDifficultySubtitle', language).replace('{{groupType}}', groupMeta.groupLabelLower)
      : t('coreUI.choose_difficulty_description', language) || t('choose_difficulty_description', language) || 'Choose the difficulty level that best matches your biblical knowledge';

  /**
   * Unified difficulty options with internationalization
   * Color-coded accents: Easy=correct(green), Medium=accent(amber), Hard=incorrect(red)
   */
  const DIFFICULTY_OPTIONS = [
    {
      id: 1,
      name: t("coreUI.easy", language) || t("easy", language) || "Easy",
      description: t("coreUI.easy_desc", language) || t("easy_desc", language) || "Great for beginners",
      color: colors.correct,
      ionicon: "leaf-outline" as const,
    },
    {
      id: 2,
      name: t("coreUI.medium", language) || t("medium", language) || "Medium",
      description: t("coreUI.medium_desc", language) || t("medium_desc", language) || "For regular players",
      color: colors.accent,
      ionicon: "flash-outline" as const,
    },
    {
      id: 3,
      name: t("coreUI.hard", language) || t("hard", language) || "Hard",
      description: t("coreUI.hard_desc", language) || t("hard_desc", language) || "For experienced explorers",
      color: colors.incorrect,
      ionicon: "diamond-outline" as const,
    }
  ];

  /**
   * Handle difficulty selection with mode-specific logic
   */
  const handleDifficultySelect = async (option: typeof DIFFICULTY_OPTIONS[0]) => {
    if (loading) return;

    // Allow custom difficulty selection handler (for special use cases)
    if (onDifficultySelect) {
      onDifficultySelect(option.id);
      return;
    }

    // Haptic feedback for enhanced UX (multiplayer mode)
    if (mode === 'multiplayer' && Platform.OS !== 'web') {
      try {
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      } catch (error) {
        // Haptics not available
      }
    }

    setSelected(option.id);
    setLoading(true);

    try {
      if (mode === 'multiplayer') {
        await handleMultiplayerDifficultySelect(option);
      } else if (mode === 'challenge') {
        handleChallengeDifficultySelect(option);
      } else {
        handleSoloDifficultySelect(option);
      }
    } catch (error) {
      if (mode === 'multiplayer') {
        const message =
          error instanceof Error && error.message
            ? error.message
            : t('multiplayer_lobby.roomCreateFailed', language);
        Alert.alert(t('error', language), message);
      }
      setLoading(false);
      setSelected(null);
    }
  };

  /**
   * Handle solo mode difficulty selection
   */
  const handleSoloDifficultySelect = (option: typeof DIFFICULTY_OPTIONS[0]) => {
    navigation.navigate('SinglePlayerGame', {
      category: category || '',
      categoryId,
      difficulty_id: option.id,
      questionCount,
      challengeMode
    });
  };

  /**
   * Handle multiplayer mode difficulty selection with room creation
   */
  const handleMultiplayerDifficultySelect = async (option: typeof DIFFICULTY_OPTIONS[0]) => {
    if (!categoryId) {
      throw new Error(t('multiplayer_lobby.roomCreateFailed', language));
    }

    if (rematch && oldRoomId) {
      // Rematch flow - create new room and copy players
      const room = await createRoom({
        host_id: userProfile.id,
        name: userProfile.name,
        nickname: userProfile.name,
        difficulty_id: option.id,
        category_id: categoryId,
        room_type: roomType,
        family_id: familyId,
        friends_group_id: friendsGroupId,
      });

      if (!room || !room.id) {
        const createError = useMultiplayerStore.getState().error;
        throw new Error(createError || t('multiplayer_lobby.roomCreateFailed', language));
      }

      setLoading(false);
      navigation.replace("GameRoom", { roomId: room.id });
    } else {
      // Regular multiplayer flow
      const room = await createRoom({
        host_id: userProfile.id,
        name: userProfile.name,
        nickname: userProfile.name,
        difficulty_id: option.id,
        category_id: categoryId,
        room_type: roomType,
        family_id: familyId,
        friends_group_id: friendsGroupId,
      });

      if (!room || !room.id) {
        const createError = useMultiplayerStore.getState().error;
        throw new Error(createError || t('multiplayer_lobby.roomCreateFailed', language));
      }

      setLoading(false);
      navigation.replace("GameRoom", { roomId: room.id });
    }
  };

  /**
   * Handle challenge mode difficulty selection
   */
  const handleChallengeDifficultySelect = (option: typeof DIFFICULTY_OPTIONS[0]) => {
    navigation.navigate('ChallengeConfirm', {
      opponent: challengeOpponent,
      categoryId: categoryId || 0,
      category: category || '',
      testament: testament || null,
      difficulty_id: option.id,
    });
  };

  /**
   * Render difficulty option with mode-specific styling
   */
  const renderDifficultyOption = (option: typeof DIFFICULTY_OPTIONS[0], _index: number) => {
    const isSelected = selected === option.id;
    const isDisabled = loading;

    const containerStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'flex-start' as const,
      alignSelf: 'stretch' as const,
      borderRadius: radius.xl,
      backgroundColor: isSelected ? 'rgba(246,190,58,0.18)' : shellColors.panel,
      marginBottom: spacing.lg,
      paddingVertical: spacing.xl,
      paddingHorizontal: spacing['2xl'],
      borderWidth: 2,
      borderColor: isSelected ? shellColors.goldBright : shellColors.border,
      flex: mode === 'solo' ? 1 : undefined,
      minWidth: 220,
      maxWidth: 400,
      shadowColor: isSelected ? shellColors.gold : colors.black,
      shadowOffset: { width: 0, height: isSelected ? 8 : 4 },
      shadowOpacity: isSelected ? 0.3 : 0.15,
      shadowRadius: isSelected ? 12 : 8,
      elevation: isSelected ? 8 : 4,
      transform: [{ scale: isSelected ? 1.02 : 1 }],
    };

    return (
      <TouchableOpacity
        key={option.id}
        style={containerStyle}
        onPress={() => handleDifficultySelect(option)}
        disabled={isDisabled}
        accessibilityLabel={option.name}
        accessibilityRole="button"
        activeOpacity={0.85}
      >
        {/* Icon Container */}
        <View style={{
          width: 56,
          height: 56,
          borderRadius: radius.lg,
          backgroundColor: isSelected ? 'rgba(246,190,58,0.18)' : option.color + '22',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: spacing.lg,
          borderWidth: 1,
          borderColor: isSelected ? shellColors.borderStrong : option.color + '55',
        }}>
          <Ionicons
            name={option.ionicon}
            size={28}
            color={isSelected ? shellColors.goldSoft : option.color}
          />
        </View>

        {/* Content Container */}
        <View style={{ flex: 1 }}>
          <DSText
            variant="title"
            color={isSelected ? shellColors.ivory : shellColors.ivory}
            style={{ marginBottom: spacing.xs }}
            adjustsFontSizeToFit
            numberOfLines={1}
          >
            {option.name}
          </DSText>
          <DSText
            variant="label"
            color={isSelected ? shellColors.goldSoft : shellColors.muted}
            style={{ lineHeight: 18 }}
            adjustsFontSizeToFit
            numberOfLines={2}
          >
            {option.description}
          </DSText>
        </View>

        {/* Multiplayer: Info button */}
        {mode === 'multiplayer' && (
          <TouchableOpacity
            onPress={() => {/* Info modal could go here */}}
            style={{ marginLeft: spacing.sm, opacity: 0.5 }}
          >
            <Ionicons name="information-circle-outline" size={18} color={shellColors.muted} />
          </TouchableOpacity>
        )}

        {/* Loading Indicator */}
        {loading && isSelected && (
          <ActivityIndicator
            size="small"
            color={isSelected ? colors.white : option.color}
            style={{ marginLeft: spacing.md }}
          />
        )}

        {/* Solo: Selection Indicator */}
        {mode === 'solo' && isSelected && !loading && (
          <View style={{
            width: 24,
            height: 24,
            borderRadius: radius.full,
            backgroundColor: shellColors.goldSoft,
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: spacing.md,
          }}>
            <Ionicons name="checkmark" size={16} color={option.color} />
          </View>
        )}
      </TouchableOpacity>
    );
  };

  // Early loading return for solo mode compatibility
  if (loading && mode === 'solo') {
    return (
      <View style={{ flex: 1, backgroundColor: shellColors.canvas, paddingHorizontal: spacing['2xl'], paddingTop: spacing['5xl'] }}>
        <DSText variant="body" color={shellColors.muted}>{t('loading', language)}</DSText>
      </View>
    );
  }

  return (
    <View style={[tw`flex-1`, { backgroundColor: shellColors.canvas }]}>

      {/* Mode Indicator - Shows current mode in dev builds */}
      {showDebugModeBadge && (
        <View style={{
          position: 'absolute',
          top: spacing['4xl'],
          right: spacing.sm,
          backgroundColor: shellColors.panel,
          borderWidth: 1,
          borderColor: shellColors.border,
          paddingHorizontal: spacing.sm,
          paddingVertical: spacing.xs,
          borderRadius: 12,
          zIndex: 10,
        }}>
          <DSText variant="micro" color={colors.onSurfaceVariant} style={{ fontWeight: '600' }}>
            {mode.toUpperCase()}
          </DSText>
        </View>
      )}

      {/* Content Container - Mode-specific layout */}
      {mode === 'solo' ? (
        // Solo Mode: ScrollView layout with SafeAreaView
        <SafeAreaView style={{ flex: 1 }}>
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              justifyContent: 'center',
              alignItems: 'center',
              padding: spacing.xl
            }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={{
              width: '100%',
              maxWidth: 400,
              minWidth: 320,
              paddingVertical: spacing['4xl'],
              paddingHorizontal: spacing.xl,
              alignItems: 'center',
              marginBottom: spacing['3xl'],
              borderRadius: spacing['3xl'],
              justifyContent: 'center',
              backgroundColor: shellColors.panelRaised,
              borderWidth: 2,
              borderColor: shellColors.borderStrong,
              ...shadows.lg,
            }}>
              {/* Header Section */}
              <View style={{
                alignItems: 'center',
                marginBottom: spacing['3xl'],
                paddingHorizontal: spacing.sm,
              }}>
              <DSText
                variant="display"
                color={shellColors.ivory}
                align="center"
                style={{
                  marginBottom: spacing.sm,
                  letterSpacing: -0.5,
                  fontWeight: '800',
                  fontSize: mode === 'multiplayer' ? 28 : undefined,
                  lineHeight: mode === 'multiplayer' ? 34 : undefined,
                  paddingHorizontal: mode === 'multiplayer' ? spacing.md : 0,
                }}
              >
                {difficultyTitle}
              </DSText>

                <DSText
                  variant="body"
                  color={shellColors.muted}
                  align="center"
                  style={{ fontWeight: '500', maxWidth: 280 }}
                >
                  {difficultySubtitle}
                </DSText>
              </View>

              {/* Difficulty Options */}
              {DIFFICULTY_OPTIONS.map((option, index) => renderDifficultyOption(option, index))}

              {/* Back Button */}
              <TouchableOpacity
                onPress={() => navigation.goBack()}
                disabled={loading}
                style={{
                  borderRadius: radius.lg,
                  backgroundColor: shellColors.goldSoft,
                  paddingVertical: spacing.lg,
                  paddingHorizontal: spacing['3xl'],
                  marginTop: spacing.sm,
                  width: '100%',
                  maxWidth: 320,
                  alignItems: 'center',
                  ...shadows.md,
                }}
                accessibilityLabel={t("coreUI.back", language) || t("back", language) || "Back"}
              >
                <DSText variant="body" color={iqesColors.navy} style={{ fontWeight: '800' }}>
                  {t("coreUI.back", language) || t("back", language) || "Back"}
                </DSText>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </SafeAreaView>
      ) : (
        // Multiplayer Mode: Centered layout
        <View style={tw`flex-1 items-center justify-center px-6`}>
          <View style={{
            width: '100%',
            maxWidth: 400,
            minWidth: 320,
            alignItems: 'center',
            padding: spacing['4xl'],
            marginBottom: spacing['3xl'],
            backgroundColor: shellColors.panelRaised,
            ...shadows.lg,
            borderWidth: 2,
            borderColor: shellColors.borderStrong,
            borderRadius: spacing['3xl'],
          }}>
            {/* Header Section */}
            <View style={{ alignItems: 'center', marginBottom: spacing['3xl'], paddingHorizontal: spacing.sm }}>
              <DSText
                variant="display"
                color={shellColors.ivory}
                align="center"
                style={{
                  marginBottom: spacing.sm,
                  letterSpacing: -0.5,
                  fontWeight: '800',
                  fontSize: mode === 'multiplayer' ? 28 : undefined,
                  lineHeight: mode === 'multiplayer' ? 34 : undefined,
                  paddingHorizontal: mode === 'multiplayer' ? spacing.md : 0,
                }}
              >
                {difficultyTitle}
              </DSText>
              <DSText
                variant="body"
                color={shellColors.muted}
                align="center"
                style={{
                  fontWeight: '500',
                  maxWidth: 320,
                  paddingHorizontal: mode === 'multiplayer' ? spacing.md : 0,
                }}
              >
                {difficultySubtitle}
              </DSText>
            </View>

            {/* Difficulty Options */}
            {DIFFICULTY_OPTIONS.map((option, index) => renderDifficultyOption(option, index))}

            {/* Back Button */}
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              disabled={loading}
              style={{
                width: '100%',
                padding: spacing.lg,
                borderRadius: radius.xl,
                marginTop: spacing.lg,
                backgroundColor: shellColors.goldSoft,
                ...shadows.md,
              }}
            >
              <DSText
                variant="body"
                color={iqesColors.navy}
                align="center"
                style={{ fontWeight: '800' }}
              >
                {"\u2190"} {t("coreUI.back", language) || t("back", language) || "Back"}
              </DSText>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

export default UnifiedDifficultyScreen;
